import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet } from '../../utils/api';
import { computeOverallStats } from '../../utils/raStats';
import { computeHrn, formatHrnPill } from '../../constants/riskAssessment';
import { formatDate } from '../../utils/dateFormat';
import { dateOnly } from '../../utils/formDiff';
import { BTN_XS } from '../../constants/buttons';
import RiskReductionBar from '../RiskReductionBar';
import LoadingSpinner from '../LoadingSpinner';

/**
 * Per-RA preview card for the gag panel's Risk Assessments section.
 *
 * The header (title / reference / status pill) reuses the EXACT markup the
 * linked-RA list used before, so the two stay visually consistent. Below it,
 * a signed-off / openable RA lazy-loads its sections the first time the section
 * is expanded (active) and shows an initial→residual reduction bar plus the
 * worst residual HRN. A draft a plain member can't read shows a muted note and
 * never hits the network (the server privacy-404s those).
 *
 * Props:
 *  - ra: { id, title, reference, status, signed_off_at, linked_by_name, created_by }
 *  - canOpen: bool — caller mirrors the server read gate (oversight / author /
 *      signed-off). Drives the title link vs plain text AND whether we fetch.
 *  - active: bool — the section is expanded. The fetch is keyed on this so a
 *      collapsed section makes NO network call.
 *  - canEdit: bool — gag editor; shows the Unlink control (unchanged behaviour).
 *  - onUnlink: fn(ra) — unlink handler (owned by GagDetailPanel; untouched here).
 *  - unlinkBusy: bool — disables the Unlink button during an in-flight mutation.
 */
export default function RaSummaryCard({ ra, canOpen, active, canEdit = false, onUnlink, unlinkBusy = false }) {
  // { loading, error, data }. data null until a fetch commits. The gate below
  // keys off data/error (not a "fetched" ref): once we hold a committed result
  // it's cached across re-expands (the body stays mounted under `hidden`), but
  // a fetch abandoned mid-flight leaves the state idle so a later re-expand
  // starts fresh — never a permanent spinner.
  const [state, setState] = useState({ loading: false, error: false, data: null });

  useEffect(() => {
    // Only fetch when open + openable and we don't already hold a result.
    // A collapsed section (active=false) or an unopenable draft never fetches.
    if (!active || !canOpen || state.data || state.error) return undefined;

    // One AbortController per run — if this run is torn down (collapse or
    // re-key) before it commits, we abort so the in-flight response is ignored
    // (at most one fetch ever commits; no duplicate concurrent commits).
    const controller = new AbortController();
    let settled = false;
    setState({ loading: true, error: false, data: null });
    (async () => {
      try {
        const res = await apiGet(`/api/risk-assessments/${ra.id}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!controller.signal.aborted) { settled = true; setState({ loading: false, error: false, data }); }
      } catch {
        if (!controller.signal.aborted) { settled = true; setState({ loading: false, error: true, data: null }); }
      }
    })();
    return () => {
      controller.abort();
      // Torn down before this run committed (e.g. collapsed mid-fetch): roll
      // the loading flag back to idle so re-expanding re-triggers the fetch
      // instead of leaving the card stuck on the spinner. A no-op on unmount
      // (React 18 ignores setState on an unmounted tree — no warning).
      if (!settled) setState({ loading: false, error: false, data: null });
    };
  }, [active, canOpen, ra.id, state.data, state.error]);

  // ── Summary derived from the loaded RA (residual stats + worst initial HRN
  //    across every hazard row). Reuses the shared HRN maths — no re-implement.
  //    The bar is a worst-initial → worst-residual summary: the two ends may
  //    come from DIFFERENT rows, but it can't visually invert (per-row residual
  //    HRN ≤ its initial, so the max residual ≤ the max initial).
  let summary = null;
  if (state.data) {
    const rows = (state.data.sections || []).flatMap((s) => s.rows || []);
    const stats = computeOverallStats(rows);
    const worstInitial = rows.reduce(
      (m, r) => Math.max(m, computeHrn(r.lo_init, r.fe_init, r.dph_init, r.np_init)),
      0
    );
    summary = { stats, worstInitial };
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 space-y-2">
      {/* Header row — same markup/classes as the old linked-RA list item. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        {canOpen ? (
          <Link
            to={`/risk-assessments/${ra.id}`}
            className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
          >
            {ra.title}
          </Link>
        ) : (
          /* The RA endpoint privacy-404s non-authors without oversight
             — plain text beats a dead link. */
          <span
            className="font-medium text-gray-800 dark:text-gray-200"
            title="Only the RA's author or oversight roles (admin/buyer/supervisor) can open this risk assessment."
          >
            {ra.title}
          </span>
        )}
        {ra.reference && (
          <span className="text-xs text-gray-500 dark:text-gray-400">[{ra.reference}]</span>
        )}
        {ra.status === 'signed_off' ? (
          <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
            {/* dateOnly first: signed_off_at is a SQLite 'YYYY-MM-DD
                HH:MM:SS' string, which formatDate alone rejects */}
            Signed off{ra.signed_off_at ? ` ${formatDate(dateOnly(ra.signed_off_at))}` : ''}
          </span>
        ) : (
          <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
            Draft
          </span>
        )}
        {ra.linked_by_name && (
          <span className="text-xs text-gray-400 dark:text-gray-500">
            Linked by {ra.linked_by_name}
          </span>
        )}
        {canEdit && onUnlink && (
          <button
            type="button"
            onClick={() => onUnlink(ra)}
            disabled={unlinkBusy}
            title="Remove the link — the risk assessment itself is not deleted"
            className={`${BTN_XS['danger-soft']} ml-auto`}
          >
            Unlink
          </button>
        )}
      </div>

      {/* Body — the lazy risk summary (only for openable RAs). */}
      {canOpen ? (
        state.loading ? (
          <LoadingSpinner size="sm" inline message="Loading risk summary…" />
        ) : state.error ? (
          <p className="text-xs text-gray-400 dark:text-gray-500 italic">
            Couldn't load risk summary.
          </p>
        ) : summary ? (
          <div className="space-y-1">
            <RiskReductionBar initialHrn={summary.worstInitial} residualHrn={summary.stats.maxResidual} />
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {summary.stats.count} hazard{summary.stats.count !== 1 ? 's' : ''} · worst residual HRN{' '}
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {formatHrnPill(summary.stats.maxResidual)}
                </span>
              </span>
              <Link
                to={`/risk-assessments/${ra.id}`}
                className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline ml-auto"
              >
                Open / Edit RA &rarr;
              </Link>
            </div>
          </div>
        ) : null
      ) : (
        <p className="text-xs text-gray-400 dark:text-gray-500 italic">
          Draft — not yet signed off.
        </p>
      )}
    </div>
  );
}
